import React from 'react';
import { Box, Stack, Typography as MuiTypography, Link } from '@mui/material';
import { Typography, TYPOGRAPHY_TOKENS, COLORS } from 'pvs-design-system';
import { AGS_SCOPES_CONFIG } from 'constants/ltiScopes';
import { LtiAdvantageFormValues } from 'types/LtiAdvantage';
import {
  EditableCheckbox,
  StyledFormControlLabel,
  DisabledCheckbox,
  DisabledFormControlLabel,
} from './styles';
import UncheckedIcon from './UncheckedIcon';

interface LtiAdvantageSectionProps {
  /** Form values containing LTI Advantage configuration */
  values: LtiAdvantageFormValues;
  /** Handler for form field changes */
  handleChange: (e: React.ChangeEvent) => void;
}

/**
 * LTI Advantage Section Component
 * Handles NRPS and AGS (Assignment and Grade Service) configuration
 */
const LtiAdvantageSection: React.FC<LtiAdvantageSectionProps> = ({
  values,
  handleChange,
}) => {
  const nrpsCheckbox = (
    <EditableCheckbox
      id="nrpsEnabled"
      checked={values.nrpsEnabled}
      onChange={handleChange}
      aria-label="NRPS (Name and Role Provisioning Service)"
    />
  );

  const agsCheckbox = (
    <EditableCheckbox
      id="agsEnabled"
      checked={values.agsEnabled}
      onChange={handleChange}
      aria-label="AGS (Assignment and Grade Service)"
    />
  );

  return (
    <>
      <Typography variant="h2" sx={TYPOGRAPHY_TOKENS.h3}>
        LTI Advantage
      </Typography>

      <Stack gap="16px">
        <StyledFormControlLabel
          control={nrpsCheckbox}
          label="NRPS (Name and Role Provisioning Service)"
        />

        <StyledFormControlLabel
          control={agsCheckbox}
          label="AGS (Assignment and Grade Service)"
        />

        {values.agsEnabled && (
          <Box sx={{ marginLeft: '32px' }}>
            <MuiTypography
              sx={{
                marginBottom: '8px',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '20px',
              }}
            >
              Scopes supported (assume the{' '}
              <Link
                id="ags-scope-url"
                href="https://purl.imsglobal.org/spec/lti-ags/scope/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: `${COLORS.purple[700]} !important`,
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '20px',
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationSkipInk: 'none',
                  textDecorationThickness: 'auto',
                  textUnderlineOffset: 'auto',
                  '&:hover': {
                    color: `${COLORS.purple[700]} !important`,
                  },
                }}
              >
                https://purl.imsglobal.org/spec/lti-ags/scope/
              </Link>{' '}
              prefix):
            </MuiTypography>
            <Stack gap="8px">
              {AGS_SCOPES_CONFIG.map((scope) => {
                // All scopes are shown as checkboxes
                // Non-disabled scopes are checked and disabled (read-only)
                // Disabled scopes (lineitem) are unchecked and disabled
                const isChecked = !scope.disabled;
                const scopeCheckbox = (
                  <DisabledCheckbox
                    id={scope.id}
                    checked={isChecked}
                    disabled
                    aria-label={scope.label}
                    icon={<UncheckedIcon />}
                  />
                );

                return (
                  <DisabledFormControlLabel
                    key={scope.id}
                    control={scopeCheckbox}
                    label={scope.label}
                  />
                );
              })}
            </Stack>
          </Box>
        )}
      </Stack>
    </>
  );
};

export default LtiAdvantageSection;
